/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\api\merchants.ts
 * 商户管理API服务
 * 
 * [2025-11-22 19:47] 新增getCurrentRestaurant和getServedWorkplaces方法
 * - 添加getCurrentRestaurant(): 获取当前商户餐厅（merchant-admin专用）
 * - 添加getServedWorkplaces(): 获取当前商户服务的职场列表
 * - 添加对应的Mock数据方法getMockCurrentRestaurant和getMockServedWorkplaces
 * - 原因: ScheduleEditForm需要这两个API，之前不存在导致404错误
 */

import { BaseApiService } from './base'
import { httpClient } from './http'
import type {
  Merchant,
  MerchantFilters,
  MerchantFormData,
  MerchantStatsData,
  PaginatedMerchantResponse,
  MerchantBrand,
  Cuisine,
  MerchantRestaurant
} from '../types/merchant'

interface MealSegmentDetail {
  code: string
  label?: string
}

interface ServedWorkplace {
  id: string
  code?: string
  name?: string
  enterprise_id?: string
  enterprise_name?: string
  address?: string
  meal_segments: MealSegmentDetail[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

function toIdString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const s = value.trim()
    return s ? s : undefined
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return undefined
}

function getViteEnvFlag(key: string): boolean {
  const env = import.meta.env as unknown
  const rec = toRecord(env)
  return String(rec[key] ?? 'false') === 'true'
}

/**
 * 商户管理API服务类
 * 提供商户相关的所有API操作
 */
class MerchantApiService extends BaseApiService<Merchant, MerchantFormData, Partial<MerchantFormData>> {
  
  protected readonly endpoint = 'merchants/'
  // ✅ 只有显式设置 VITE_USE_MOCK=true 才允许回退 Mock（避免未配置时默认误用 Mock 导致“只剩3条数据”的错觉）
  private readonly useMock: boolean = getViteEnvFlag('VITE_USE_MOCK')
  

  /**
   * 获取商户列表
   * 输入格式：筛选条件对象，包含分页、排序、筛选参数
   * 输出意义：返回分页的商户列表数据
   */
  async getMerchants(params?: MerchantFilters & {
    page?: number
    page_size?: number
    ordering?: string
  }): Promise<PaginatedMerchantResponse> {
    try {
      // 重要说明：企业模块已使用原生 axios 返回原始数据结构，
      // 这里保持一致，避免 httpClient 对统一响应结构的二次解包造成 undefined
      const response = await httpClient
        .getAxiosInstance()
        .get<PaginatedMerchantResponse | { data: PaginatedMerchantResponse }>(this.endpoint, { params })
      const raw = response.data as unknown
      
      // 归一化处理：支持多种响应格式
      const normalized: unknown = (() => {
        if (isRecord(raw) && Array.isArray(raw.results)) return raw
        if (isRecord(raw) && isRecord(raw.data) && Array.isArray((raw.data as Record<string, unknown>).results)) return raw.data
        return raw
      })()
      
      console.log('[MerchantAPI] getMerchants 成功响应(归一化):', normalized)
      
      // 防御：若结构异常则走 mock
      if (!isRecord(normalized) || !Array.isArray(normalized.results)) {
        throw new Error('Invalid merchant list response shape')
      }
      
      // 补充缺失的分页字段（DRF 默认分页不返回 page/page_size/total_pages）
      const page = params?.page || 1
      const pageSize = params?.page_size || 20
      const count = typeof normalized.count === 'number' ? normalized.count : (normalized.results as unknown[]).length
      
      const result: PaginatedMerchantResponse = {
        results: normalized.results as PaginatedMerchantResponse['results'],
        count: count,
        next: typeof normalized.next === 'string' ? normalized.next : undefined,
        previous: typeof normalized.previous === 'string' ? normalized.previous : undefined,
        page: page,
        page_size: pageSize,
        total_pages: Math.ceil(count / pageSize)
      }
      
      return result
    } catch (error) {
      // ⚠️ 关键修复：禁止“默认回退 Mock”
      // - 未显式开启 VITE_USE_MOCK 时，真实接口失败应直接抛错，让页面展示真实错误而不是假数据
      if (this.useMock) {
        console.log('[MerchantAPI] VITE_USE_MOCK=true，API请求失败，回退Mock数据:', error)
        const mockData = await this.getMockMerchants(params)
        console.log('[MerchantAPI] Mock数据结构:', mockData)
        return mockData
      }
      throw error
    }
  }

  /**
   * 获取商户详情
   * 输入格式：商户ID字符串
   * 输出意义：返回指定商户的详细信息
   */
  async getMerchantById(id: string): Promise<Merchant> {
    try {
      return await httpClient.get<Merchant>(`${this.endpoint}${id}/`)
    } catch (error) {
      // Mock数据回退
      if (this.useMock) {
        return this.getMockMerchantById(id)
      }
      throw error
    }
  }

  /**
   * 创建商户
   * 输入格式：商户表单数据对象
   * 输出意义：返回创建成功的商户信息
   */
  async createMerchant(data: MerchantFormData): Promise<Merchant> {
    try {
      const payload = this.transformFormDataToPayload(data)
      return await httpClient.post<Merchant>(this.endpoint, payload)
    } catch (error) {
      // Mock数据回退
      if (this.useMock) {
        return this.createMockMerchant(data)
      }
      throw error
    }
  }

  /**
   * 更新商户
   * 输入格式：商户ID和部分更新数据
   * 输出意义：返回更新后的商户信息
   */
  async updateMerchant(
    id: string,
    data: Partial<MerchantFormData>,
    baseServiceConfigurations?: Record<string, unknown> | null
  ): Promise<Merchant> {
    try {
      const payload = this.transformFormDataToPayload(data, baseServiceConfigurations)
      return await httpClient.patch<Merchant>(`${this.endpoint}${id}/`, payload)
    } catch (error) {
      // Mock数据回退
      if (this.useMock) {
        return this.updateMockMerchant(id, data)
      }
      throw error
    }
  }

  /**
   * 删除商户
   * 输入格式：商户ID字符串
   * 输出意义：删除成功无返回值
   */
  async deleteMerchant(id: string): Promise<void> {
    try {
      await httpClient.delete(`${this.endpoint}${id}/`)
    } catch (error) {
      // Mock数据回退
      if (this.useMock) {
        return this.deleteMockMerchant()
      }
      throw error
    }
  }

  /**
   * 获取商户统计数据
   * 输入格式：筛选条件对象（可选）
   * 输出意义：返回商户统计信息，用于筛选器指标展示
   */
  async getMerchantStats(filters?: MerchantFilters): Promise<MerchantStatsData> {
    try {
      // 预清理空参数，避免出现 undefined 等噪音参数
      const params: Record<string, unknown> = { ...(filters || {}) }
      Object.keys(params).forEach((k) => {
        const v = params[k]
        if (v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)) {
          delete params[k]
        }
      })

      // ✅ 直接请求后端 merchants/stats/ 端点（已实装）
      const data = await httpClient.get<MerchantStatsData>(`${this.endpoint}stats/`, { params })
      return data
    } catch (error) {
      console.error('[MerchantAPI] 获取商户统计失败，返回空数据:', error)
      return this.getEmptyMerchantStats()
    }
  }

  /**
   * 返回空的商户统计数据（后端请求失败时的降级）
   */
  private getEmptyMerchantStats(): MerchantStatsData {
    return {
      merchantTotal: 0,
      restaurantTotal: 0,
      statuses: {
        pendingMerchants: 0,
        activeMerchants: 0,
        inactiveMerchants: 0,
        rejectedMerchants: 0,
      },
      qualifications: {
        completeMerchants: 0,
        incompleteMerchants: 0,
        pendingMerchants: 0,
        expiredMerchants: 0,
        noneMerchants: 0,
      },
      regions: {},
      brands: {},
      cuisines: {},
    }
  }

  /**
   * 获取商户品牌列表
   * 输入格式：无参数或筛选条件
   * 输出意义：返回可选的品牌列表
   */
  async getMerchantBrands(): Promise<MerchantBrand[]> {
    try {
      // 统一走 constantsAPI 结构：/api/v1/constants/merchant-brands/
      // 兼容 httpClient 返回 {data|results|raw} 的归一化
      const resp = await httpClient.getAxiosInstance().get('constants/merchant-brands/', { params: { is_active: true, active_only: true } })
      const raw = resp.data as unknown
      const items: unknown[] = Array.isArray(raw)
        ? raw
        : (isRecord(raw) && Array.isArray(raw.results) ? raw.results : (isRecord(raw) && Array.isArray(raw.data) ? raw.data : []))
      return (items || []) as unknown as MerchantBrand[]
    } catch (error) {
      // Mock数据回退 - 开发环境默认使用Mock数据
      console.log('品牌API请求失败，使用Mock数据:', error)
      return this.getMockMerchantBrands()
    }
  }

  /**
   * 获取菜系列表
   * 输入格式：无参数或筛选条件
   * 输出意义：返回可选的菜系列表
   */
  async getCuisines(): Promise<Cuisine[]> {
    try {
      const resp = await httpClient.getAxiosInstance().get('constants/cuisines/', { params: { is_active: true, active_only: true } })
      const raw = resp.data as unknown
      const items: unknown[] = Array.isArray(raw)
        ? raw
        : (isRecord(raw) && Array.isArray(raw.results)
          ? raw.results
          : (isRecord(raw) && Array.isArray(raw.data)
            ? raw.data
            : []))
      return (items || []) as unknown as Cuisine[]
    } catch (error) {
      // Mock数据回退 - 开发环境默认使用Mock数据
      console.log('菜系API请求失败，使用Mock数据:', error)
      return this.getMockCuisines()
    }
  }

  /**
   * 获取商户餐厅列表
   * 输入格式：商户ID
   * 输出意义：返回指定商户的餐厅列表
   */
  async getMerchantRestaurants(merchantId: string): Promise<MerchantRestaurant[]> {
    try {
      return await httpClient.get<MerchantRestaurant[]>(`${this.endpoint}${merchantId}/restaurants/`)
    } catch (error) {
      // Mock数据回退
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockMerchantRestaurants(merchantId)
      }
      throw error
    }
  }

  /**
   * 获取当前商户的餐厅（merchant-admin专用）
   * 输入格式：无（从登录用户自动识别）
   * 输出意义：返回当前登录商户管理员的餐厅信息
   */
  async getCurrentRestaurant(): Promise<MerchantRestaurant> {
    try {
      // 后端API：GET /api/v1/merchants/restaurants/
      // 返回当前商户的餐厅（单个对象或数组的第一个）
      const response = await httpClient.getAxiosInstance().get<MerchantRestaurant[] | MerchantRestaurant>('merchants/restaurants/')
      const raw: unknown = response.data as unknown
      
      // 归一化处理（可能是数组或单个对象）
      if (Array.isArray(raw)) {
        if (raw.length === 0) {
          throw new Error('当前商户没有关联餐厅')
        }
        return raw[0]
      }
      
      // 如果是嵌套在data中
      if (isRecord(raw) && raw.data) {
        const data = raw.data
        if (Array.isArray(data)) {
          if (data.length === 0) {
            throw new Error('当前商户没有关联餐厅')
          }
          return data[0] as MerchantRestaurant
        }
        if (isRecord(data)) return data as unknown as MerchantRestaurant
      }
      
      if (isRecord(raw)) return raw as unknown as MerchantRestaurant
      throw new Error('Invalid current restaurant response shape')
    } catch (error) {
      console.error('[MerchantAPI] 获取当前餐厅失败:', error)
      // Mock数据回退
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockCurrentRestaurant()
      }
      throw error
    }
  }

  /**
   * 获取当前商户所服务的职场列表
   * ✅ M5-FIX-P4: 支持 operator-admin（传入 workplaceIds 进行筛选）
   * ✅ M14-FIX: 支持双场景（线上订餐 ALWS01 + 职场分餐 CLGR01）
   * 输入格式：workplaceIds（可选，职场ID数组）
   * 输出意义：返回指定职场的 EMS 配置（包含餐段）
   */
  async getServedWorkplaces(workplaceIds?: string[]): Promise<ServedWorkplace[]> {
    try {
      // 1. 通过 EnterpriseMerchantService API 获取服务关系
      // ⭐ M14-FIX: 支持线上预点双场景（ALWS01 线上订餐 + CLGR01 职场分餐）
      const { emsApi } = await import('./enterprise_merchant_services')
      
      // ⭐ M14-FIX: 支持线上预点的消费场景（参考 order-type-consumption-mapping.md）
      const ONLINE_PREORDER_SCENES = new Set(['ALWS01', 'CLGR01'])
      
      let response: unknown
      
      // ⚠️ 策略A：后端将强制必填 consumption_type + is_active
      // - 本函数语义为“线上预点相关职场”（ALWS01 + CLGR01），因此按两次请求分别拉取再合并
      if (workplaceIds && workplaceIds.length === 1) {
        // 单个职场：直接用 workplace_id 参数（后端支持）
        const r1 = await emsApi.list({
          workplace_id: workplaceIds[0],
          is_active: true,
          consumption_type: 'ALWS01',
        })
        const r2 = await emsApi.list({
          workplace_id: workplaceIds[0],
          is_active: true,
          consumption_type: 'CLGR01',
        })
        response = [...r1, ...r2]
        console.log(`[MerchantAPI] 📡 EMS API (单个职场 ${workplaceIds[0]}) 返回数据:`, response)
      } else {
        // 多个职场或无筛选：获取所有（operator-admin 初始状态 / merchant-admin）
        const r1 = await emsApi.list({ is_active: true, consumption_type: 'ALWS01' })
        const r2 = await emsApi.list({ is_active: true, consumption_type: 'CLGR01' })
        response = [...r1, ...r2]
        console.log('[MerchantAPI] 📡 EMS API (全部) 返回数据:', response)
      }
      
      console.log('[MerchantAPI] 📡 EMS API 返回数据:', response)
      console.log('[MerchantAPI] 📡 response类型:', typeof response, 'isArray:', Array.isArray(response))
      const respRec = toRecord(response)
      console.log('[MerchantAPI] 📡 response.results:', respRec.results)
      console.log('[MerchantAPI] 📡 response.data:', respRec.data)
      
      // 处理分页格式 {count, next, previous, results} 或直接数组
      let emsRecords: unknown[] = []
      if (Array.isArray(response)) {
        emsRecords = response
        console.log('[MerchantAPI] ✅ 使用数组格式，记录数:', emsRecords.length)
      } else if (isRecord(response) && Array.isArray(response.results)) {
        emsRecords = response.results
        console.log('[MerchantAPI] ✅ 使用分页格式 response.results，记录数:', emsRecords.length)
      } else if (isRecord(response) && isRecord(response.data) && Array.isArray(response.data.results)) {
        emsRecords = response.data.results
        console.log('[MerchantAPI] ✅ 使用嵌套格式 response.data.results，记录数:', emsRecords.length)
      } else if (isRecord(response) && Array.isArray(response.data)) {
        emsRecords = response.data
        console.log('[MerchantAPI] ✅ 使用嵌套格式 response.data，记录数:', emsRecords.length)
      } else {
        console.error('[MerchantAPI] ❌ 未知的响应格式，无法解析EMS记录')
        console.error('[MerchantAPI] response结构:', JSON.stringify(response, null, 2))
      }
      
      if (emsRecords.length === 0) {
        console.warn('[MerchantAPI] ⚠️ 未找到任何EMS服务关系记录')
        console.warn('[MerchantAPI] ⚠️ 请检查: 1) 商户是否有EMS数据')
        return []
      }
      
      // ⭐ M14-FIX: 前端过滤消费场景（只保留支持线上预点的场景）
      const beforeFilter = emsRecords.length
      emsRecords = emsRecords.filter((record: unknown) => {
        const r = toRecord(record)
        const scene = typeof r.consumption_type === 'string' ? r.consumption_type : ''
        return ONLINE_PREORDER_SCENES.has(scene)
      })
      console.log(`[MerchantAPI] 🔍 消费场景过滤: ${beforeFilter} → ${emsRecords.length} 条（线上订餐+职场分餐）`)
      
      console.log(`[MerchantAPI] 📋 EMS记录数: ${emsRecords.length}`, emsRecords)
      
      // 2. 从EMS记录中提取职场信息（使用嵌套的 workplace_detail）
      const workplaceMap = new Map<string, ServedWorkplace>()
      emsRecords.forEach((record: unknown) => {
        const r = toRecord(record)
        const wp = toRecord(r.workplace_detail)
        const wpId = toIdString(wp.id)
        if (wpId) {
          // ✅ M5-FIX: 如果传入了 workplaceIds，则只处理这些职场
          if (workplaceIds && workplaceIds.length > 0 && !workplaceIds.includes(wpId)) {
            return // 跳过不匹配的职场
          }

          console.log('[MerchantAPI] 📍 职场详情:', wp)
          
          // 合并餐段信息
          const existingWp = workplaceMap.get(wpId)
          const rawMealSegments = Array.isArray(r.meal_segments_detail) ? r.meal_segments_detail : []
          const mealSegments: MealSegmentDetail[] = rawMealSegments
            .map((ms): MealSegmentDetail | null => {
              const m = toRecord(ms)
              const code = typeof m.code === 'string' ? m.code : undefined
              if (!code) return null
              const label = typeof m.label === 'string' ? m.label : undefined
              return label ? { code, label } : { code }
            })
            .filter((x): x is MealSegmentDetail => x !== null)
          
          console.log('[MerchantAPI] 🍽️ 餐段信息:', mealSegments)
          
          if (existingWp) {
            // 合并餐段（去重）
            const existingCodes = new Set(existingWp.meal_segments.map((ms) => ms.code))
            mealSegments.forEach((ms) => {
              if (!existingCodes.has(ms.code)) {
                existingWp.meal_segments.push(ms)
              }
            })
          } else {
            workplaceMap.set(wpId, {
              id: wpId,
              code: toIdString(wp.code),
              name: typeof wp.name === 'string' ? wp.name : undefined,
              enterprise_id: toIdString(wp.enterprise_id),
              enterprise_name: typeof wp.enterprise_name === 'string' ? wp.enterprise_name : undefined,
              address: typeof wp.street_address === 'string' ? wp.street_address : undefined,
              meal_segments: [...mealSegments]
            })
          }
        } else {
          console.warn('[MerchantAPI] ⚠️ EMS记录缺少 workplace_detail:', record)
        }
      })
      
      // 3. 转换为数组
      const servedWorkplaces = Array.from(workplaceMap.values())
      
      console.log(`[MerchantAPI] ✅ 获取职场列表成功: ${servedWorkplaces.length} 个职场`, servedWorkplaces)
      return servedWorkplaces
    } catch (error) {
      console.error('[MerchantAPI] 获取职场列表失败:', error)
      return [] // 失败时返回空数组
    }
  }

  /**
   * 获取餐厅已存储的收款二维码（只读查询，不会生成新码）
   * 输入格式：restaurantId（餐厅 ID，string|number）
   * 输出意义：返回已持久化的二维码数据；如果尚未生成则 qrcode_data 为空字符串
   */
  async getRestaurantQrCode(restaurantId: string | number): Promise<{
    qrcode_data: string
    restaurant_id: string
    restaurant_name: string
    merchant_name: string
    operator_id: string
    version: string
    is_permanent: boolean
    has_qrcode: boolean
    generated_at: string | null
  }> {
    const resp = await httpClient.getAxiosInstance().get(
      `merchants/restaurants/${restaurantId}/get-qrcode/`
    )
    const raw: unknown = resp.data
    const payload = isRecord(raw) && isRecord(raw.data) ? raw.data : raw
    const p = toRecord(payload)
    return {
      qrcode_data: typeof p.qrcode_data === 'string' ? p.qrcode_data : '',
      restaurant_id: String(p.restaurant_id ?? ''),
      restaurant_name: typeof p.restaurant_name === 'string' ? p.restaurant_name : '',
      merchant_name: typeof p.merchant_name === 'string' ? p.merchant_name : '',
      operator_id: String(p.operator_id ?? ''),
      version: typeof p.version === 'string' ? p.version : '',
      is_permanent: p.is_permanent === true,
      has_qrcode: p.has_qrcode === true,
      generated_at: typeof p.generated_at === 'string' ? p.generated_at : null,
    }
  }

  /**
   * 生成（或重新生成）餐厅维度收款二维码，结果持久化到后端 DB
   * 输入格式：restaurantId（餐厅 ID，string|number）
   * 输出意义：返回新生成的二维码数据字符串和餐厅信息，用于前端渲染二维码图片
   */
  async generateRestaurantQrCode(restaurantId: string | number): Promise<{
    qrcode_data: string
    restaurant_id: string
    restaurant_name: string
    merchant_name: string
    operator_id: string
    version: string
    is_permanent: boolean
  }> {
    const resp = await httpClient.getAxiosInstance().post(
      `merchants/restaurants/${restaurantId}/generate-qrcode/`
    )
    const raw: unknown = resp.data
    // 归一化：兼容 {data: {...}} 或直接 {...}
    const payload = isRecord(raw) && isRecord(raw.data) ? raw.data : raw
    const p = toRecord(payload)
    return {
      qrcode_data: typeof p.qrcode_data === 'string' ? p.qrcode_data : '',
      restaurant_id: String(p.restaurant_id ?? ''),
      restaurant_name: typeof p.restaurant_name === 'string' ? p.restaurant_name : '',
      merchant_name: typeof p.merchant_name === 'string' ? p.merchant_name : '',
      operator_id: String(p.operator_id ?? ''),
      version: typeof p.version === 'string' ? p.version : '',
      is_permanent: p.is_permanent === true,
    }
  }

  // ==================== Mock数据方法 ====================

  /**
   * Mock商户列表数据
   */
  private async getMockMerchants(params?: Record<string, unknown>): Promise<PaginatedMerchantResponse> {
    // 模拟API延迟
    await new Promise(resolve => setTimeout(resolve, 300))
    
    const mockMerchants: Merchant[] = [
      {
        id: 'merchant_001',
        name: '老北京炸酱面',
        merchant_id: 'M001',
        status: 'active',
        province: '北京市',
        city: '北京市',
        region: '朝阳区',
        street_address: '三里屯街道工体北路8号',
        latitude: 39.9042,
        longitude: 116.4074,
        restaurant_contact_person: '张师傅',
        phone_number: '13800138001',
        restaurant_contact_email: 'zhang@example.com',
        business_hours: '09:00-21:00',
        average_rating: 4.5,
        commission_rate: 0.08,
        provides_commission_invoice: true,
        qualification_status: 'complete',
        health_certificate_count: 3,
        created_at: '2024-01-15T10:30:00Z',
        updated_at: '2024-08-20T14:20:00Z',
        brand: {
          id: 'brand_004',
          code: 'DDPP',
          label: '当地品牌',
          description: '当地知名品牌',
          sort_order: 4,
          status: 'APPROVED',
          is_active: true,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        cuisine: {
          id: 'cuisine_001',
          code: 'ZSKC',
          label: '中式快餐',
          description: '中式快餐类别',
          sort_order: 1,
          status: 'APPROVED',
          is_active: true,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        signer: {
          id: 'user_001',
          username: 'wang_bd',
          full_name: '王商拓'
        }
      },
      {
        id: 'merchant_002',
        name: '川味香锅',
        merchant_id: 'M002',
        status: 'pending',
        province: '上海市',
        city: '上海市',
        region: '黄浦区',
        street_address: '南京东路100号',
        latitude: 31.2304,
        longitude: 121.4737,
        restaurant_contact_person: '李老板',
        phone_number: '13800138002',
        business_hours: '11:00-22:00',
        average_rating: 4.2,
        commission_rate: 0.10,
        provides_commission_invoice: false,
        qualification_status: 'incomplete',
        health_certificate_count: 2,
        created_at: '2024-08-01T09:15:00Z',
        updated_at: '2024-08-25T16:45:00Z',
        brand: {
          id: 'brand_003',
          code: 'QYPP',
          label: '区域品牌',
          description: '区域性连锁品牌',
          sort_order: 3,
          status: 'APPROVED',
          is_active: true,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        cuisine: {
          id: 'cuisine_004',
          code: 'CXCX',
          label: '川湘菜系',
          description: '川菜湘菜等辣味菜系',
          sort_order: 4,
          status: 'APPROVED',
          is_active: true,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        signer: {
          id: 'user_002',
          username: 'li_bd',
          full_name: '李商拓'
        }
      },
      {
        id: 'merchant_003',
        name: '粤式茶餐厅',
        merchant_id: 'M003',
        status: 'active',
        province: '广东省',
        city: '广州市',
        region: '天河区',
        street_address: '天河路208号',
        latitude: 23.1291,
        longitude: 113.2644,
        restaurant_contact_person: '陈经理',
        phone_number: '13800138003',
        business_hours: '07:00-23:00',
        average_rating: 4.8,
        commission_rate: 0.06,
        provides_commission_invoice: true,
        qualification_status: 'complete',
        health_certificate_count: 5,
        created_at: '2024-02-10T08:20:00Z',
        updated_at: '2024-08-28T11:30:00Z',
        brand: {
          id: 'brand_002',
          code: 'QGPP',
          label: '全国品牌',
          description: '全国连锁知名品牌',
          sort_order: 2,
          status: 'APPROVED',
          is_active: true,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        cuisine: {
          id: 'cuisine_005',
          code: 'YSCX',
          label: '粤式菜系',
          description: '广东粤菜系',
          sort_order: 5,
          status: 'APPROVED',
          is_active: true,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        account_manager: {
          id: 'user_003',
          username: 'zhao_op',
          full_name: '赵运营'
        }
      }
    ]

    // 简单的筛选逻辑
    let filteredMerchants = mockMerchants
    if (params && typeof params.keyword === 'string' && params.keyword.trim()) {
      const keyword = params.keyword.toLowerCase()
      filteredMerchants = filteredMerchants.filter(m => 
        m.name.toLowerCase().includes(keyword) || 
        m.restaurant_contact_person.toLowerCase().includes(keyword)
      )
    }
    if (params && typeof params.status === 'string') {
      filteredMerchants = filteredMerchants.filter(m => m.status === params.status)
    }
    if (params && typeof params.province === 'string') {
      filteredMerchants = filteredMerchants.filter(m => m.province === params.province)
    }

    // 分页逻辑
    const page = params && typeof params.page === 'number' ? params.page : 1
    const pageSize = params && typeof params.page_size === 'number' ? params.page_size : 20
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    const paginatedResults = filteredMerchants.slice(startIndex, endIndex)

    return {
      results: paginatedResults,
      count: filteredMerchants.length,
      page,
      page_size: pageSize,
      total_pages: Math.ceil(filteredMerchants.length / pageSize),
      next: endIndex < filteredMerchants.length ? `page=${page + 1}` : undefined,
      previous: page > 1 ? `page=${page - 1}` : undefined
    }
  }

  /**
   * Mock商户详情数据
   */
  private async getMockMerchantById(id: string): Promise<Merchant> {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    const merchants = await this.getMockMerchants()
    const merchant = merchants.results.find(m => m.id === id)
    if (!merchant) {
      throw new Error(`Merchant with id ${id} not found`)
    }
    return merchant
  }

  /**
   * Mock创建商户
   */
  private async createMockMerchant(data: MerchantFormData): Promise<Merchant> {
    await new Promise(resolve => setTimeout(resolve, 500))
    
    const newMerchant: Merchant = {
      id: `merchant_${Date.now()}`,
      name: data.basic_info.name,
      merchant_id: `M${Date.now().toString().slice(-3)}`,
      status: 'pending',
      province: data.basic_info.province,
      city: data.basic_info.city,
      region: data.basic_info.region,
      street_address: data.basic_info.street_address,
      latitude: data.basic_info.location?.lat,
      longitude: data.basic_info.location?.lng,
      restaurant_contact_person: data.contact_info.restaurant_contact_person,
      phone_number: data.contact_info.phone_number,
      restaurant_contact_email: data.contact_info.restaurant_contact_email,
      average_rating: 5.0,
      provides_commission_invoice: false,
      qualification_status: 'incomplete',
      health_certificate_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    
    return newMerchant
  }

  /**
   * Mock更新商户
   */
  private async updateMockMerchant(id: string, data: Partial<MerchantFormData>): Promise<Merchant> {
    await new Promise(resolve => setTimeout(resolve, 400))
    
    const merchant = await this.getMockMerchantById(id)
    // 简单的更新逻辑
    if (data.basic_info) {
      Object.assign(merchant, {
        name: data.basic_info.name || merchant.name,
        province: data.basic_info.province || merchant.province,
        city: data.basic_info.city || merchant.city,
        region: data.basic_info.region || merchant.region,
        street_address: data.basic_info.street_address || merchant.street_address
      })
    }
    merchant.updated_at = new Date().toISOString()
    return merchant
  }

  /**
   * Mock删除商户
   */
  private async deleteMockMerchant(): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300))
    // Mock删除成功，无需实际操作
  }

  /**
   * Mock商户统计数据
   */
  private async getMockMerchantStats(): Promise<MerchantStatsData> {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    return {
      merchantTotal: 156,
      restaurantTotal: 203,
      statuses: {
        pendingMerchants: 23,
        activeMerchants: 118,
        inactiveMerchants: 12,
        rejectedMerchants: 3
      },
      qualifications: {
        completeMerchants: 95,
        incompleteMerchants: 41,
        pendingMerchants: 15,
        expiredMerchants: 5,
        noneMerchants: 0,
      },
      regions: {
        '北京市': 45,
        '上海市': 38,
        '广州市': 32,
        '深圳市': 25,
        '杭州市': 16
      },
      brands: {
        '集配央厨': 35,
        '全国品牌': 42,
        '区域品牌': 28,
        '当地品牌': 31,
        '小规模': 18,
        '其他': 2
      },
      cuisines: {
        '中式快餐': 48,
        '西式快餐': 32,
        '轻食沙拉': 18,
        '川湘菜系': 25,
        '粤式菜系': 20,
        '本帮菜系': 8,
        '其他特色': 5
      }
    }
  }

  /**
   * Mock品牌数据
   */
  private async getMockMerchantBrands(): Promise<MerchantBrand[]> {
    await new Promise(resolve => setTimeout(resolve, 150))
    
    return [
      { 
        id: 'brand_001', 
        code: 'JPYC', 
        label: '集配央厨', 
        description: '中央厨房集配模式',
        sort_order: 1,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'brand_002', 
        code: 'QGPP', 
        label: '全国品牌', 
        description: '全国连锁知名品牌',
        sort_order: 2,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'brand_003', 
        code: 'QYPP', 
        label: '区域品牌', 
        description: '区域性连锁品牌',
        sort_order: 3,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'brand_004', 
        code: 'DDPP', 
        label: '当地品牌', 
        description: '当地知名品牌',
        sort_order: 4,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'brand_005', 
        code: 'XGMM', 
        label: '小规模', 
        description: '小规模经营',
        sort_order: 5,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'brand_006', 
        code: 'OTHER', 
        label: '其他', 
        description: '其他类型品牌',
        sort_order: 6,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      }
    ]
  }

  /**
   * Mock菜系数据
   */
  private async getMockCuisines(): Promise<Cuisine[]> {
    await new Promise(resolve => setTimeout(resolve, 150))
    
    return [
      { 
        id: 'cuisine_001', 
        code: 'ZSKC', 
        label: '中式快餐', 
        description: '中式快餐类别',
        sort_order: 1,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'cuisine_002', 
        code: 'XSKC', 
        label: '西式快餐', 
        description: '西式快餐类别',
        sort_order: 2,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'cuisine_003', 
        code: 'QSSL', 
        label: '轻食沙拉', 
        description: '健康轻食沙拉',
        sort_order: 3,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'cuisine_004', 
        code: 'CXCX', 
        label: '川湘菜系', 
        description: '川菜湘菜等辣味菜系',
        sort_order: 4,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'cuisine_005', 
        code: 'YSCX', 
        label: '粤式菜系', 
        description: '广东粤菜系',
        sort_order: 5,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'cuisine_006', 
        code: 'BBCX', 
        label: '本帮菜系', 
        description: '上海本帮菜系',
        sort_order: 6,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      { 
        id: 'cuisine_007', 
        code: 'QTTS', 
        label: '其他特色', 
        description: '其他特色菜系',
        sort_order: 7,
        status: 'APPROVED',
        is_active: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      }
    ]
  }

  /**
   * Mock商户餐厅数据
   */
  private async getMockMerchantRestaurants(merchantId: string): Promise<MerchantRestaurant[]> {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    return [
      {
        id: 'restaurant_001',
        merchant_id: merchantId,
        name: '三里屯店',
        province: '北京市',
        city: '北京市',
        region: '朝阳区',
        street_address: '三里屯街道工体北路8号',
        latitude: 39.9042,
        longitude: 116.4074,
        is_active: true,
        created_at: '2024-01-15T10:30:00Z',
        updated_at: '2024-08-20T14:20:00Z'
      }
    ]
  }

  /**
   * Mock当前餐厅数据
   */
  private async getMockCurrentRestaurant(): Promise<MerchantRestaurant> {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    return {
      id: 'restaurant_test_001',
      merchant_id: 'merchant_test_001',
      name: '测试餐厅_川菜馆',
      province: '北京市',
      city: '北京市',
      region: '朝阳区',
      street_address: '朝阳路123号',
      latitude: 39.9042,
      longitude: 116.4074,
      is_active: true,
      created_at: '2024-01-15T10:30:00Z',
      updated_at: '2024-08-20T14:20:00Z'
    }
  }

  /**
   * Mock职场列表数据
   */
  private async getMockServedWorkplaces(): Promise<ServedWorkplace[]> {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    return [
      {
        id: 'workplace_001',
        name: '朝阳园区',
        enterprise_name: '测试企业_建筑装饰2',
        address: '朝阳区建国路88号',
        meal_segments: []
      },
      {
        id: 'workplace_002',
        name: '海淀园区',
        enterprise_name: '测试企业_建筑装饰2',
        address: '海淀区中关村大街1号',
        meal_segments: []
      },
      {
        id: 'workplace_003',
        name: '通州园区',
        enterprise_name: '测试企业_建筑装饰2',
        address: '通州区运河大街99号',
        meal_segments: []
      }
    ]
  }

  /**
   * 转换表单数据为API载荷
   * 输入格式：前端表单数据结构
   * 输出意义：转换为后端API接受的数据格式
   */
  private transformFormDataToPayload(
    data: Partial<MerchantFormData>,
    baseServiceConfigurations?: Record<string, unknown> | null
  ): Record<string, unknown> {
    const payload: Record<string, unknown> = {}

    /**
     * 输入：开户行 code（如 ICBC）
     * 输出：开户行 label（如 工商银行）；未知则返回原值字符串
     */
    const normalizeBankLabel = (code: unknown): string => {
      const raw = (code === undefined || code === null) ? '' : String(code).trim()
      if (!raw) return ''
      const map: Record<string, string> = {
        ICBC: '工商银行',
        CCB: '建设银行',
        ABC: '农业银行',
        BOC: '中国银行',
        BOCOM: '交通银行',
        CMB: '招商银行',
        SPDB: '浦发银行',
        CMBC: '民生银行',
        CIB: '兴业银行',
        PAB: '平安银行',
        OTHER: '其他',
      }
      return map[raw] || raw
    }

    /**
     * 输入：可能为 string/number/undefined 的员工ID
     * 输出：可提交给后端的整数ID（不可用则返回 undefined）
     */
    const toIntId = (v: unknown): number | undefined => {
      const n = Number(v)
      if (!Number.isFinite(n) || n <= 0) return undefined
      return Math.trunc(n)
    }

    /**
     * 输入：对象 payload
     * 输出：剔除空字符串/undefined/null/空数组后的 payload（避免后端对 email 等字段校验）
     */
    const omitEmpty = (obj: Record<string, unknown>): Record<string, unknown> => {
      const out: Record<string, unknown> = {}
      Object.keys(obj || {}).forEach((k) => {
        const v = obj[k]
        if (v === undefined || v === null) return
        if (typeof v === 'string' && v.trim() === '') return
        if (Array.isArray(v) && v.length === 0) return
        out[k] = v
      })
      return out
    }

    /**
     * service_configurations 是后端 JSONField，PATCH 时语义为“整体替换”。
     * 分Tab提交若只带一个 section，会覆盖掉其它 section（如 business_info 会覆盖 supply_capability）。
     * 这里用“旧值 + 本次变更”合并后整体提交，保证互不覆盖。
     */
    const mergeServiceConfigurations = (patch: Record<string, unknown>): void => {
      const base =
        baseServiceConfigurations && typeof baseServiceConfigurations === 'object' && !Array.isArray(baseServiceConfigurations)
          ? baseServiceConfigurations
          : {}
      const existing = toRecord(payload['service_configurations'])
      payload['service_configurations'] = { ...base, ...existing, ...patch }
    }

    /**
     * 佣金率换算（商户模型字段为“比例”，UI 为“百分比”）
     * 输入：percent（0-100），例如 18
     * 输出：ratio（0-1），例如 0.18
     *
     * 说明：FinanceInfoStep.vue 右侧固定展示“%”，所以这里统一按百分比解释。
     */
    const percentToRatio = (v: unknown): number | undefined => {
      if (v === undefined || v === null || v === '') return undefined
      const n = Number(v)
      if (!Number.isFinite(n)) return undefined
      // 百分比范围保护（允许 0-100）
      const pct = Math.max(0, Math.min(100, n))
      // ratio 保留 4 位小数足够（与后端 DecimalField 常见精度一致）
      const ratio = pct / 100
      return Number(ratio.toFixed(4))
    }
    
    if (data.basic_info) {
      Object.assign(payload, {
        name: data.basic_info.name,
        printer_id: data.basic_info.printer_id,
        // ✅ 对齐 OpenAPI MerchantRequest：operator_city 为 int（OperatorCity.id）
        operator_city: toIntId((data.basic_info as unknown as { operator_city?: unknown }).operator_city),
        province: data.basic_info.province,
        city: data.basic_info.city,
        region: data.basic_info.region,
        street_address: data.basic_info.street_address,
        latitude: data.basic_info.location?.lat,
        longitude: data.basic_info.location?.lng,
        description: data.basic_info.description
      })

      // ✅ 契约对齐：品牌为顶层外键字段（Merchant.brand）
      const brandId = toIntId((data.basic_info as unknown as { brand_id?: unknown }).brand_id)
      if (brandId !== undefined) payload['brand'] = brandId

      // ✅ 长期契约：餐厅名称直接提交给后端 Merchant.default_restaurant_name（OpenAPI 暴露）
      // - 前端字段名：basic_info.restaurant_name
      // - 后端字段名：default_restaurant_name
      const restaurantName = String((data.basic_info as unknown as { restaurant_name?: unknown }).restaurant_name || '').trim()
      if (restaurantName) {
        payload['default_restaurant_name'] = restaurantName
      }
    }
    
    if (data.contact_info) {
      const contact = toRecord(data.contact_info)
      const isAdmin = contact.is_admin !== false
      const contactName = contact.restaurant_contact_person
      const contactPhone = contact.phone_number
      const adminName = isAdmin ? contactName : contact.admin_name
      const adminPhone = isAdmin ? contactPhone : contact.admin_phone

      Object.assign(payload, {
        restaurant_contact_person: contactName,
        phone_number: contactPhone,
        restaurant_contact_email: contact.restaurant_contact_email,
        // ✅ 创建/编辑商户时同步创建/维护商户管理员（与创建企业逻辑一致）
        is_admin: isAdmin,
        admin_name: adminName,
        admin_phone: adminPhone,
      })
    }
    
    if (data.finance_info) {
      const finance = toRecord(data.finance_info)
      const bankLabel = normalizeBankLabel(finance.bank_name)
      const branch = finance.bank_branch_name
      const mergedBankBranchName = (() => {
        const b = (branch === undefined || branch === null) ? '' : String(branch).trim()
        const label = (bankLabel || '').trim()
        if (label && b) return `${label} ${b}`
        if (label) return label
        if (b) return b
        return undefined
      })()

      Object.assign(payload, {
        bank_account_name: (data.finance_info as unknown as { bank_account_name?: unknown }).bank_account_name,
        bank_account_number: (data.finance_info as unknown as { bank_account_number?: unknown }).bank_account_number,
        // 后端当前仅有 bank_branch_name（“开户银行及支行”），前端拆成“开户行 + 支行”：
        // - 优先保存合并值（银行label + 支行）
        // - 同时将 bank_name / 结算等扩展字段落入 service_configurations 便于回显
        bank_branch_name: mergedBankBranchName,
        // ✅ P0: UI 录入为百分比（18），后端存储为比例（0.18）
        commission_rate: percentToRatio((data.finance_info as unknown as { commission_rate?: unknown }).commission_rate),
        provides_commission_invoice: (data.finance_info as unknown as { three_flow_unified?: unknown }).three_flow_unified
      })

      const financeCfg = omitEmpty({
        bank_name: finance.bank_name,
        tax_category: finance.tax_category,
        tax_rate: finance.tax_rate,
        settlement_method: finance.settlement_method,
        settlement_cycle: finance.settlement_cycle,
        security_deposit: finance.security_deposit,
        contract_entity: finance.contract_entity,
        invoice_entity: finance.invoice_entity,
        payment_entity: finance.payment_entity,
      })
      if (Object.keys(financeCfg).length > 0) {
        mergeServiceConfigurations({ finance_info: financeCfg })
      }
    }

    // 商拓信息（人员关联）：
    // - 后端 MerchantRequest 接收字段为 signer/account_manager（integer）
    // - 前端表单字段为 business_info.signer_id/supervisor_id（string）
    // ✅ 统一口径（避免两套负责制）：
    // - “商拓负责人(signer_id)”是商户负责制人员，必须落到 account_manager（后端列表负责制过滤字段）
    // - signer 字段保留为“创建人/签约人”展示信息（可与 account_manager 同人）
    // - supervisor_id 不属于后端核心字段，仅落入 service_configurations 以便回显
    if (data.business_info) {
      const biz = toRecord(data.business_info)
      const signer = toIntId(biz.signer_id)
      if (signer !== undefined) {
        payload['signer'] = signer
        payload['account_manager'] = signer
      }

      // 其余商拓字段当前后端未建模，统一落到 service_configurations 以便保存/回显
      const bizCfg = omitEmpty({
        signer_id: biz.signer_id,
        signer_phone: biz.signer_phone,
        signer_level: biz.signer_level,
        join_date: biz.join_date,
        supervisor_id: biz.supervisor_id,
        supervisor_level: biz.supervisor_level,
        department: biz.department,
        responsible_area: biz.responsible_area,
        target_merchants: biz.target_merchants,
        commission_percentage: biz.commission_percentage,
        notes: biz.notes,
      })
      if (Object.keys(bizCfg).length > 0) {
        mergeServiceConfigurations({ business_info: bizCfg })
      }
    }
    
    // 供应能力（表单 Step4）：
    // - 后端 Merchant 有 business_hours；其余“供应能力”字段暂不在模型中，先落入 service_configurations 以避免数据丢失。
    if (data.supply_capability) {
      const supply = toRecord(data.supply_capability)
      if (typeof supply.business_hours === 'string') {
        payload['business_hours'] = supply.business_hours
      }

      // ✅ 契约对齐：菜系为顶层外键字段（Merchant.cuisine）
      const cuisineId = toIntId((supply as Record<string, unknown>).cuisine_id)
      if (cuisineId !== undefined) payload['cuisine'] = cuisineId

      // ⭐ R5: single_meal_capacity → 新后端顶层字段 meal_supply_capacity
      const rawCap = (supply as Record<string, unknown>).single_meal_capacity
      if (rawCap === undefined || rawCap === null || rawCap === '') {
        payload['meal_supply_capacity'] = null
      } else {
        const capNum = Number(rawCap)
        payload['meal_supply_capacity'] = Number.isFinite(capNum) ? capNum : null
      }

      const supplyCfg = omitEmpty({
        ...supply,
        // 避免重复：business_hours 已落到顶层字段
        business_hours: undefined,
        // 避免重复：cuisine 已落到顶层字段
        cuisine_id: undefined,
        // 避免重复：single_meal_capacity 已落到顶层 meal_supply_capacity
        single_meal_capacity: undefined,
      })
      if (Object.keys(supplyCfg).length > 0) {
        mergeServiceConfigurations({ supply_capability: supplyCfg })
      }
    }

    // 配送能力（表单 Step5）：
    // - 后端 Merchant.delivery_personnel_info 为 JSONField：用于保存配送员列表
    // - 其余配送配置落入 service_configurations，便于后续扩展（不引入额外字段迁移）
    if (data.delivery_capability) {
      const delivery = toRecord(data.delivery_capability)
      const personnel = delivery.delivery_personnel
      if (Array.isArray(personnel)) {
        payload['delivery_personnel_info'] = personnel
      }
      const deliveryCfg = omitEmpty({
        ...delivery,
        // 避免重复：配送员列表已落到 delivery_personnel_info
        delivery_personnel: undefined,
      })
      if (Object.keys(deliveryCfg).length > 0) {
        mergeServiceConfigurations({ delivery_capability: deliveryCfg })
      }
    }

    // 资质资料（Step7）：
    // - 文档文件本体已走 legal-documents 入库（multipart）
    // - 这里仅保存“表单侧的结构化字段/回显字段”（URL/编号/日期等），落入 service_configurations 防止丢失
    if (data.qualification) {
      const q = toRecord(data.qualification)
      const health = q.health_certificates
      const qCfg = omitEmpty({ ...q })
      // 防御：禁止把本地 DataURL(base64) 写入 service_configurations（会导致 DB 膨胀）
      Object.keys(qCfg).forEach((k) => {
        const v = qCfg[k]
        if (typeof v === 'string' && v.startsWith('data:')) delete qCfg[k]
      })
      // 健康证列表即使为空也保留（避免 patch 时被 omitEmpty 丢掉）
      if (Array.isArray(health)) {
        qCfg.health_certificates = (health as unknown[]).map((it) => {
          const r = toRecord(it)
          const url = typeof r.url === 'string' && r.url.startsWith('data:') ? '' : r.url
          return omitEmpty({ ...r, url })
        })
      }
      if (Object.keys(qCfg).length > 0) {
        mergeServiceConfigurations({ qualification: qCfg })
      }
    }

    // 实景拍照（Step8）：
    // - 部分照片已写入 Merchant.ImageField（storefront_photo/lobby_photo/kitchen_photo）
    // - 其余扩展字段暂作为 service_configurations.photos 保存（用于编辑页回显/后续扩展）
    if (data.photos) {
      const p = toRecord(data.photos)
      const tools = p.delivery_tool_photos
      const pCfg = omitEmpty({ ...p })
      // 防御：禁止把本地 DataURL(base64) 写入 service_configurations（会导致 DB 膨胀）
      Object.keys(pCfg).forEach((k) => {
        const v = pCfg[k]
        if (typeof v === 'string' && v.startsWith('data:')) delete pCfg[k]
      })
      if (Array.isArray(tools)) {
        pCfg.delivery_tool_photos = (tools as unknown[]).map((it) => {
          const r = toRecord(it)
          const url = typeof r.url === 'string' && r.url.startsWith('data:') ? '' : r.url
          return omitEmpty({ ...r, url })
        })
      }
      if (Object.keys(pCfg).length > 0) {
        mergeServiceConfigurations({ photos: pCfg })
      }
    }
    
    return omitEmpty(payload)
  }
}

// 导出单例实例
export const merchantApi = new MerchantApiService()
export default merchantApi

// 重新导出类型
export type {
  Merchant,
  MerchantFilters,
  MerchantFormData,
  MerchantStatsData,
  MerchantBrand,
  Cuisine,
  MerchantRestaurant,
  PaginatedMerchantResponse,
  MerchantTableColumnConfig
} from '../types/merchant'
